import { Injectable } from "@angular/core";
import { ProductPatchDto } from "@sharedcommon/model/product-patch.model";
import {
  ComputeDto,
  ComputeServiceClient,
  ComputeWithMetaDataDto,
  DataResultColumnFilterDto,
  DataSampleNutrientService,
  DataSampleTempCalculationServiceClient,
  EntityServiceClient,
  GenericResultFilterDto,
  PredictionModelDto,
  RawMaterialDto,
  RawMaterialNutrientDto,
  RawMaterialService,
  ReferenceDto,
  ReferenceServiceClient,
  ResultColumnService,
  ResultDto,
  ResultFilterDto,
  SampleNutrientDto,
  SampleResultFilterDto,
  SampleResultService
} from "@sharedcommon/rest/data.restservice";
import { UserService } from "@sharedcommon/services/user.service";
import { CalculationStatus } from "@sharedcommon/model/calculation-status.model";
import { RawMaterialOrSampleNutrientValueTableData } from "@sharedcommon/model/raw-material-or-sample-table-value-data.model";
import { ResultColumnDto } from "@sharedcommon/model/result-column.model";
import { SampleDto } from "@sharedcommon/model/sample/sample.model";
import { SampleRestService } from "@sharedcommon/rest/sample/sample.restservice";
import { Observable, of, switchMap } from "rxjs";
import { CalculationStatusService } from "./calculation-status.service";
import { SampleTempCalculationRestService } from "@sharedcommon/rest/sample/sample-temp-calculation.restservice";
import { ResultSettingsService } from "./result-settings.service";
import { UserResultPrefValueType } from "@sharedcommon/model/user.result-pref.model";
import { SampleResultColumnFilterDto } from "@sharedcommon/model/sample-result-column-filter.model";
import { ImportType } from "@sharedcommon/model/import-type.model";
import { URN } from "@sharedcommon/model/urn.model";
import { GenericResultDetailFilterModel } from "@sharedcommon/model/generic-result-detail-filter.model";
import { RmOrSampleMultiModificationStore } from "@usercommon/stores/rm-or-sample-multi-modification.store";

/**
 * Service created to mutualize information from raw material or sample (depending on the operating mode).
 * This is used to store information about raw material or sample
 * AND to direct the API call to the right service according the operating mode
 */
@Injectable()
export class RawmaterialOrSampleService {

  /**
   * The table data used to show and story data entered by the user in the
   * values tab which display the component NeqRawmaterialsOrSamplesComponent
   */
  private values: RawMaterialOrSampleNutrientValueTableData[];
  /** the current temporary calculation */
  private calculationId: string = null;
  /** sample or raw mat ID */
  private productId: string = null;
  private notes: string = null;
  private error: any = null;
  private coherence: number = null;
  private isCoherent: boolean;
  /** The calculation status from the current product (raw mat or sample) */
  private productCalculationStatus: CalculationStatus = null ;
  /** The temporary calculation status (which is the returned body of calculation) */
  private tempCalculationStatus: CalculationStatus = null ;
  /** All the result column wich are at the origin of blocked calculation */
  private allBlockedResults: ResultColumnDto[] = null ;
  /** The raw mat or sample ID to duplicate */
  private productIdToDuplicate: string ;
  /** Indicates if the status from temporary calculation (=> the temp calculation result) has been forced */
  private tempForcedStatus : boolean = false ;
  /** Indicates if the raw material/sample have been saved at least once */
  private saved: boolean = false;

  private computeWithMeta: ComputeWithMetaDataDto ;

  private reference: ReferenceDto;
  private predictionModel: PredictionModelDto;
  private referenceAreChanged: boolean;

  private referenceOfSampleOrRawMat: ReferenceDto;

  constructor(
    private readonly sampleService: SampleRestService,
    private readonly sampleResultService: SampleResultService,
    private readonly computeService: ComputeServiceClient,
    private readonly dataTempCalcService: DataSampleTempCalculationServiceClient,
    private readonly sampleTempCalcService: SampleTempCalculationRestService,
    private readonly rawMatService: RawMaterialService,
    private readonly entityService: EntityServiceClient,
    private readonly sampleNutService: DataSampleNutrientService,
    private readonly referenceService: ReferenceServiceClient,
    private readonly resultColumnService: ResultColumnService,
    private readonly resultSettingsService: ResultSettingsService,
    private readonly userService: UserService,
    private readonly rmOrSampleMultiModificationStore: RmOrSampleMultiModificationStore,
  ) {
    this.clearValues(true);
  }

  /**
   * Retrieve the raw material or the sample (depending on the operating mode)
   *
   * @param sampleOrRawMatId the ID of the raw mat or the sample to retrieve
   * @returns the retrieved sample or raw mat
   */
  retrieveProductById(sampleOrRawMatId: string): Observable<SampleDto|RawMaterialDto> {
    if (this.isFeedMode()) {
      return this.rawMatService.get(this.userService.getEntity(), sampleOrRawMatId);
    } else {
      return this.sampleService.getSampleById(sampleOrRawMatId);
    }
  }

  /**
   * Patch the sample or the raw material
   *
   * @param sampleOrRawMatId the sample or raw mat to patch
   * @param patchDto         the patch DTO to send
   * @returns the API response
   */
  patch(sampleOrRawMatId: string, patchDto: ProductPatchDto): Observable<void> {
    if (this.userService.getEntity()) {
      if (this.isFeedMode()) {
        patchDto.rawMatIds = Array.of(sampleOrRawMatId);
        return this.rawMatService.patch(this.userService.getEntity(), patchDto);
      } else {
        return this.sampleService.patch(sampleOrRawMatId, patchDto);
      }
    } else {
      return of();
    }
  }

  /**
   * Save temporary calculation into the sample or raw mat AND update all meta data (as reference, notes and coherence)
   *
   * @param tempCalculationId the temporary calculation ID which correspong to the compute ID in raw mat
   * @param sampleId the sample ID, if it is about a sample
   * @returns the save temp API responses success
   */
  saveTempCalcAndUpdateAllMetaDatas(
    tempCalculationId: string,
    rawMatOrSample: RawMaterialDto|SampleDto
  ) : Observable<void> {
      // If reference to have changed save it before saving values.
      if (this.referenceAreChanged) {
        return this.updateReferenceOfRawMatOrSample(rawMatOrSample)
          .pipe(
            switchMap((_) => {
              // The reference has just been updated then the reference of sample or raw mat
              // becomes the selected ones
              this.referenceAreChanged = false;
              this.referenceOfSampleOrRawMat = this.reference;
              return this.saveTempCalAndUpdateNotesAndCohenrence(tempCalculationId, rawMatOrSample)
            })
          );
      } else {
        return this.saveTempCalAndUpdateNotesAndCohenrence(tempCalculationId, rawMatOrSample);
      }
  }

  /**
   * Save the temp calculation and update notes and coherence
   *
   * @param tempCalculationId   the calculation ID to save
   * @param rawMaterialOrSample the raw mat or sample to handle and to update
   */
  private saveTempCalAndUpdateNotesAndCohenrence(
    tempCalculationId: string, rawMatOrSample: RawMaterialDto|SampleDto
  ): Observable<void> {
    return new Observable<void>(observer => {
      this.saveTempCalculation(tempCalculationId, rawMatOrSample.id)
        .subscribe({
          next: () => {
            this.handleSaveTempSuccess(rawMatOrSample);
            // In case we are in multiple modification we set the current rm in saved states
            this.rmOrSampleMultiModificationStore.setCurrentRmOrSampleAsSaved();
            this.patchNoteAndCoherence(rawMatOrSample.id).subscribe({
                next: () => {
                    observer.next();
                    observer.complete();
                },
                error: (patchError) => {
                    observer.error(patchError);
                    observer.complete();
                }
            });
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    })
  }

  /**
   * Handle the success of save temporary results
   *
   * @param rawMaterialOrSample the raw mat or sample to handle
   */
  private handleSaveTempSuccess(rawMaterialOrSample: RawMaterialDto|SampleDto): void {
    rawMaterialOrSample.forcedCalcStatus = this.getTempForcedCalcStatus();
    rawMaterialOrSample.calculationStatus = this.getTempCalculationStatus();
    rawMaterialOrSample.calculationDate = Date.now();
    if (this.userService.isFeedOperatingMode()) {
      // As the user has saved with this window then the raw material is a manual import
      // Only for feed mode where the import type can be updated
      (rawMaterialOrSample as RawMaterialDto).importType = ImportType.MANUAL;
    }
    this.setProductCalculationStatus(rawMaterialOrSample.calculationStatus);
    this.setTempCalculationStatus(null);
    this.setTempForcedCalcStatus(false);
    this.setSaved(true);
  }

  /**
   * Patch the raw mat or sample to update its notes AND coherence
   *
   * @param rawMatOrSampleId the ID of the sample or raw mat
   * @returns Observable that completes when patches are done
  */
  private patchNoteAndCoherence(rawMatOrSampleId: string): Observable<void> {
   const coherenceValue = (this.getCoherence()) ? this.getCoherence().toString() : null;
   return this.patch(rawMatOrSampleId, {patchAction: 'SAVE_NOTES', patchValue: this.getNotes() })
   .pipe(
      switchMap(() => this.patch(rawMatOrSampleId, {patchAction: 'SAVE_COHERENCE', patchValue: coherenceValue }))
    );
  }

  /**
   * Update the reference of the raw material or sample
   *
   * @param rawMatOrSample the raw mat or the sample where to update the reference
   * @returns the observable of the API response
   */
  private updateReferenceOfRawMatOrSample(rawMatOrSample: RawMaterialDto|SampleDto): Observable<RawMaterialDto|SampleDto> {
    if (!this.isFeedMode()) {
      const sample: SampleDto = { ...rawMatOrSample as SampleDto };
      sample.reference = new URN();
      sample.reference.identifier = this.reference.code;
      // reset document and inputs to be sure to do not erase it in back
      sample.documents = null;
      sample.inputs = null;
      return this.sampleService.update(sample.id, sample, false);
    } else {
      const rm: RawMaterialDto = { ...rawMatOrSample as RawMaterialDto };
      rm.referenceId = this.reference.id;
      rm.referenceLabel = this.reference.label;
      rm.referencePredictionModelLabel = this.reference.predictionModelLabel;
      return this.rawMatService.put(this.userService.getEntity(), rawMatOrSample.id, rm);
    }
  }

  /**
   * Save temporary calculation into the sample or raw mat
   *
   * @param tempCalculationId the temporary calculation ID which correspong to the compute ID in raw mat
   * @param sampleId the sample ID, if it is about a sample
   * @returns the API response
   */
  private saveTempCalculation(tempCalculationId: string, sampleId?: string): Observable<void> {
    if (this.isFeedMode()) {
      return this.computeService.saveSave(tempCalculationId);
    } else {
      return this.dataTempCalcService.saveTemporaryCalculationAndGenerateBulletin(tempCalculationId, sampleId);
    }
  }

  /**
   * Retrieve the Reference linked to raw material or the sample
   *
   * @param rawMatOrSample the raw material or sample obj from what the object must be retrieved
   * @returns the reference attached to the raw materail or the sample
   */
  retrieveReference(rawMatOrSample: RawMaterialDto|SampleDto): Observable<ReferenceDto> {
    if (this.isFeedMode()) {
      const rawMat: RawMaterialDto = rawMatOrSample as RawMaterialDto;
      return this.entityService.referencesGet(this.userService.getEntity(), rawMat.referenceId, { translated: true});
    } else {
      const sample: SampleDto = rawMatOrSample as SampleDto;
      return this.referenceService.getByCode(sample.reference.identifier, { translated: true});
    }
  }

  /**
   * Send the force calc status request
   *
   * @param tempCalcId the temporary calculation ID to force (correspond to the compute ID)
   * @returns the compute with the forced status
   */
  forceCalcStatus(tempCalcId: string): Observable<ComputeDto> {
    if (this.isFeedMode()) {
      return this.computeService.forceCalcStatus(tempCalcId);
    } else {
      return this.sampleTempCalcService.forceCalcStatus(tempCalcId);
    }
  }

  /**
   * Retrieve the raw material or sample nutrients info
   *
   * @param entityCode       the entity code from what the right must be handled
   * @param sampleOrRawMatId the raw mat or sample ID to retrieve
   * @param reference        the reference from what the informations must be linked
   * @returns the nutrients information
   */
  retrieveProductNutrientsByEntityCode(entityCode: string, sampleOrRawMatId: string, reference: ReferenceDto): Observable<(RawMaterialNutrientDto|SampleNutrientDto)[]> {
    if (this.isFeedMode()) {
      return this.rawMatService.getAllRawMatNutrients(entityCode, sampleOrRawMatId, reference ? reference.id : null);
    } else {
      return this.sampleNutService.getSampleNutrientByEntityCode(entityCode, sampleOrRawMatId, reference ? reference.code : null);
    }
  }

  /**
   * Retrieve the results which is at the origin of the calculation blockage
   *
   * @param sampleOrRawMatId the raw mat or the sample ID
   * @param tempCalcId       the temporary calculation ID if there is
   * @returns the result which is at the origin of the blockage
   */
  retrieveBlockedResult(sampleOrRawMatId: string, tempCalcId: string): Observable<ResultColumnDto[]> {
    let matixCodeForSaved: string = undefined ;
    let matixCodeForCalculated: string = undefined ;
    for(const resultPerf of this.resultSettingsService.getResultPrefs()) {
      if (resultPerf.valueType === UserResultPrefValueType.CALCULATED) {
        matixCodeForCalculated = resultPerf.matrix.code ;
      } else if (resultPerf.valueType === UserResultPrefValueType.SAVED) {
        matixCodeForSaved = resultPerf.matrix.code ;
      }
    }
    const matrixCode = (tempCalcId ? matixCodeForCalculated : matixCodeForSaved) || this.userService.getMainMatrix()?.code;
    if (this.isFeedMode()) {
      const resultColumnFilter = new DataResultColumnFilterDto();
      if (tempCalcId) {
        resultColumnFilter.computeId = tempCalcId;
      }
      resultColumnFilter.rawMaterialId = sampleOrRawMatId;
      resultColumnFilter.matrixCode = matrixCode;
      resultColumnFilter.ceilOrFloorReached = true;
      return this.resultColumnService.fetchResultsFromRawMat(this.userService.getEntity(), sampleOrRawMatId, resultColumnFilter);
    } else {
      const resultColumnFilter = new SampleResultColumnFilterDto();
      if (tempCalcId) {
        resultColumnFilter.tempCalculationId = tempCalcId;
      }
      resultColumnFilter.sampleId = sampleOrRawMatId;
      resultColumnFilter.matrixCode = matrixCode;
      resultColumnFilter.ceilOrFloorReached = true;
      return this.resultColumnService.fetchResultsFromSample(this.userService.getEntity(), sampleOrRawMatId, resultColumnFilter);
    }
  }

  /**
   * Retrieve the results linked to the sample and raw mat
   *
   * @param entityCode       the entity code from what the nutrient must hava right
   * @param rawMatOrSampleId the raw mat or sample ID from what the result must be linked
   * @param classIds         the class IDs from what the nutrients of the result must be linked
   * @param metaClassNutIds  the met class IDs from what the nutrients of the result must be linked
   * @param filterDetails    filter details of the main filter
   * @param calculationId    the temporary calc ID if there is one
   * @returns the list of results
   */
  retrieveResults(
    entityCode: string,
    rawMatOrSampleId:string,
    classIds: string[],
    metaClassNutIds: string[],
    filterDetails: GenericResultDetailFilterModel[],
    calculationId: string
  ): Observable<ResultDto[]> {
    if (this.isFeedMode()) {
      const filter = new ResultFilterDto();
      RawmaterialOrSampleService.fillResultFilter(filter, classIds, metaClassNutIds, filterDetails);
      if (calculationId) {
        filter.computeId = calculationId;
      }
      return this.entityService.rawMaterialsResultsFetch(entityCode, rawMatOrSampleId, filter)
    } else {
      const filter = new SampleResultFilterDto();
      RawmaterialOrSampleService.fillResultFilter(filter, classIds, metaClassNutIds, filterDetails);
      if (calculationId) {
        filter.tempCalculationId = calculationId;
      }
      return this.sampleResultService.fetch(entityCode, rawMatOrSampleId, filter);
    }
  }

  /**
   * Fill the result filter
   *
   * @param filter          the result filter to fill
   * @param classIds        the class IDs from what the result must be linked
   * @param metaClassNutIds the meta class IDs from what the result must be linked
   * @param filterDetails   filter details of the main filter
   */
  private static fillResultFilter(
    filter: GenericResultFilterDto,
    classIds: string[],
    metaClassNutIds: string[],
    filterDetails: GenericResultDetailFilterModel[]
  ): void {
    filter.classNutIds = classIds;
    filter.metaClassNutIds = metaClassNutIds;
    filter.resultDetails = filterDetails.map(detail => JSON.stringify(detail));
  }

  public static returnImportType(rawMatOrSample: RawMaterialDto|SampleDto): ImportType {
    return (rawMatOrSample as RawMaterialDto)?.importType ?? (rawMatOrSample as SampleDto)?.originalImportType
  }

  /**
   * Check if we are in feed mode
   *
   * @returns true if we are in feed mode
   */
  private isFeedMode(): boolean {
    return this.userService.isFeedOperatingMode();
  }

  getValues(): RawMaterialOrSampleNutrientValueTableData[] {
    return this.values;
  }

  setValues(nutrients: RawMaterialOrSampleNutrientValueTableData[]) {
    this.values = nutrients;
  }

  getCalculationId(): string {
    return this.calculationId;
  }

  setCalculationId(id: string): void {
    this.calculationId = id;
  }

  setProductCalculationStatus(calcStat: CalculationStatus): void {
    this.productCalculationStatus = calcStat ;
  }

  getProductCalculationStatus(): CalculationStatus {
    return this.productCalculationStatus ;
  }

  setTempCalculationStatus(calcStat: CalculationStatus): void {
    this.tempCalculationStatus = calcStat ;
  }

  getTempCalculationStatus(): CalculationStatus {
    return this.tempCalculationStatus ;
  }

  getAllBlockedResults(): ResultColumnDto[] {
    return this.allBlockedResults ;
  }

  setAllBlockedResults(allResultColumns: ResultColumnDto[]): void {
    this.allBlockedResults = allResultColumns ;
  }

  /**
   * Get the calculation with the highest priority
   * @returns the calculation with the highest priority
   */
  getCurrentCalculationStatus(): CalculationStatus {
    // If the calc status of compute (which has the highest priority) is empty
    // then return the rawMat one which has the lowest priority
    return CalculationStatusService.isCalcStatusEmpty(this.tempCalculationStatus) ?
      this.productCalculationStatus : this.tempCalculationStatus ;
  }

  getProductId(): string {
    return this.productId;
  }

  setProductId(id: string): void {
    this.productId = id;
  }

  getNotes(): string {
    return this.notes;
  }

  setNotes(notes: string) {
    this.notes = notes;
  }

  getCoherence(): number {
    return this.coherence;
  }

  setCoherence(value: number) {
    this.coherence = value;
  }

  getError(): any {
    return this.error;
  }

  setError(error: any) {
    this.error = error;
  }

  setIsCoherent(coherent: boolean) {
    this.isCoherent = coherent;
  }

  getIsCoherent() {
    return this.isCoherent;
  }

  getComputeWithMeta(): ComputeWithMetaDataDto {
    return this.computeWithMeta ;
  }

  setComputeWithMeta(value: ComputeWithMetaDataDto): void {
    this.computeWithMeta = value ;
  }

  saveValues(nutrients: RawMaterialOrSampleNutrientValueTableData[], notes: string, coherence: number) {
    this.values = nutrients;
    this.storeNoteAndCoherence(notes, coherence);
  }

  storeNoteAndCoherence(notes: string, coherence: number): void {
    this.notes = notes;
    this.coherence = coherence;
  }

  setProductIdToDuplicate(value: string): void {
    this.productIdToDuplicate = value ;
  }

  getProductIdToDuplicate(): string {
    return this.productIdToDuplicate ;
  }

  setTempForcedCalcStatus(value: boolean): void {
    this.tempForcedStatus = value ;
  }

  getTempForcedCalcStatus(): boolean {
    return this.tempForcedStatus ;
  }

  setSaved(value: boolean): void {
    this.saved = value;
  }

  isSaved(): boolean {
    return this.saved;
  }

  clearValues(allData: boolean = false): void {
    /** @NB do not reset rawMatIdToDuplicate because let this action to the right component to reset it */
    if (allData) {
      this.tempForcedStatus = null;
      this.saved = null;
      this.referenceAreChanged = false;
      this.reference = null;
      this.predictionModel = null;
    }
    this.values = [];
    this.calculationId = null;
    this.productId = null;
    this.notes = null;
    this.error = null;
    this.coherence = null;
    this.isCoherent = null;
    this.productCalculationStatus = null;
    this.tempCalculationStatus = null ;
    this.allBlockedResults = null ;
    this.computeWithMeta = null ;
  }

  setReference(reference: ReferenceDto) {
    this.reference = reference;
  }

  getReference(): ReferenceDto {
    return this.reference;
  }

  setReferenceOfSampleOrRawMat(reference: ReferenceDto): void {
    this.referenceOfSampleOrRawMat = reference;
  }

  getReferenceOfSampleOrRawMat(): ReferenceDto {
    return this.referenceOfSampleOrRawMat;
  }

  setPredictionModel(predictionModel: PredictionModelDto):void {
    this.predictionModel = predictionModel;
  }

  getPredictionModel():PredictionModelDto {
    return this.predictionModel ;
  }

  setReferenceAreChanged(referenceAreChanged: boolean): void {
    this.referenceAreChanged = referenceAreChanged;
  }

  getReferenceAreChanged(): boolean {
    return this.referenceAreChanged;
  }
}
